import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { analytics } from '../lib/analytics';
import MoonLogo from '../components/MoonLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Terminal = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState({
    total_opportunities: 0,
    high_confidence_count: 0,
    top_spread: 0,
    total_volume: 0
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  
  const [signalFilter, setSignalFilter] = useState('ALL');
  const [minConfidence, setMinConfidence] = useState(0);
  
  const observerTarget = useRef(null);

  // Track terminal view
  useEffect(() => {
    analytics.terminalView();
  }, []);

  // Fetch opportunities from API
  const fetchOpportunities = useCallback(async (reset = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    const currentOffset = reset ? 0 : offset;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/markets/opportunities`, {
        params: {
          limit: 20,
          offset: currentOffset,
          signal_filter: signalFilter !== 'ALL' ? signalFilter : null,
          min_confidence: minConfidence
        }
      });
      
      const data = response.data;
      // Guarda contra resposta que não é a API (ex.: SPA fallback devolvendo
      // HTML): sem isto, um deploy com backend fora do ar vira tela branca.
      const list = Array.isArray(data?.opportunities) ? data.opportunities : [];

      if (reset) {
        setOpportunities(list);
        setOffset(20);
      } else {
        setOpportunities(prev => [...prev, ...list]);
        setOffset(prev => prev + list.length);
      }
      
      setHasMore(Boolean(data?.has_more));
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setIsLoading(false);
    }
  }, [offset, signalFilter, minConfidence, isLoading]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/markets/stats`);
      if (typeof response.data?.top_spread === 'number') {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchOpportunities(true);
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchOpportunities(true);
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch when filters change
  useEffect(() => {
    fetchOpportunities(true);
  }, [signalFilter, minConfidence]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          fetchOpportunities(false);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [observerTarget, hasMore, isLoading, fetchOpportunities]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchOpportunities(true);
    fetchStats();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  return (
    <div className="min-h-screen bg-[#06070d]">
      {/* Terminal Header */}
      <div className="bg-black/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-gray-400 hover:text-white"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <div className="h-8 w-px bg-white/10" />
              <div className="flex items-center space-x-2">
                <MoonLogo size={32} />
                <span className="text-white font-semibold text-lg tracking-wide">moon<span className="text-[#f2e2a6]">.land</span> Terminal</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#f2e2a6] rounded-full animate-pulse" />
                <span className="text-[#f2e2a6] text-sm font-mono">LIVE</span>
              </div>
              <Button
                onClick={handleRefresh}
                className="bg-white/10 text-white hover:bg-white/20"
                disabled={isRefreshing}
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Terminal Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-xl p-4">
            <div className="text-gray-400 text-sm mb-1">Total Opportunities</div>
            <div className="text-white text-2xl font-bold" data-testid="stat-total">{stats.total_opportunities}</div>
          </div>
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-xl p-4">
            <div className="text-gray-400 text-sm mb-1">High Confidence</div>
            <div className="text-[#f2e2a6] text-2xl font-bold" data-testid="stat-high-confidence">{stats.high_confidence_count}</div>
          </div>
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-xl p-4">
            <div className="text-gray-400 text-sm mb-1">Top Spread</div>
            <div className="text-[#f2e2a6] text-2xl font-bold" data-testid="stat-top-spread">{stats.top_spread.toFixed(2)}%</div>
          </div>
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-xl p-4">
            <div className="text-gray-400 text-sm mb-1">Total Volume</div>
            <div className="text-white text-2xl font-bold" data-testid="stat-volume">${stats.total_volume.toFixed(0)}M</div>
          </div>
        </div>

        {/* Opportunities Table */}
        <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <TrendingUp className="w-6 h-6 mr-2 text-[#f2e2a6]" />
                Live Trading Signals
              </h2>
              <div className="text-gray-400 text-sm">
                Showing: <span className="text-white" data-testid="opportunities-count">{opportunities.length}</span> opportunities
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <span className="text-gray-400 text-sm">Signal:</span>
                {['ALL', 'BUY', 'SELL', 'NEUTRAL'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setSignalFilter(filter)}
                    data-testid={`filter-${filter.toLowerCase()}`}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      signalFilter === filter
                        ? 'bg-[#f2e2a6] text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-gray-400 text-sm">Min Confidence:</span>
                <select
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(parseFloat(e.target.value))}
                  data-testid="confidence-select"
                  className="bg-white/10 text-white px-4 py-2 rounded-lg border border-white/20 focus:outline-none focus:border-[#f2e2a6]"
                >
                  <option value="0">All (0%)</option>
                  <option value="0.5">50%+</option>
                  <option value="0.6">60%+</option>
                  <option value="0.7">70%+</option>
                  <option value="0.8">80%+</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sem scroll interno: no telefone, scroll dentro de scroll é o que
              torna a lista impossível de usar — a página inteira rola e o
              infinite scroll observa o viewport. Tabela só em ecrã largo;
              no telemóvel cada oportunidade é um cartão. */}
          <div className="md:hidden divide-y divide-white/5" data-testid="opportunities-cards">
            {opportunities.map((opp, index) => (
              <div key={`card-${opp.id}-${index}`} className="p-4">
                <div className="text-white font-medium mb-2">{opp.title}</div>
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    opp.signal?.direction === 'BUY'
                      ? 'bg-green-500/20 text-green-400'
                      : opp.signal?.direction === 'SELL'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {opp.signal?.direction || 'NEUTRAL'} {((opp.signal?.confidence || 0) * 100).toFixed(0)}%
                  </span>
                  <span className="text-[#f2e2a6] font-bold text-lg">{opp.spread.toFixed(2)}%</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-gray-400">{opp.platform1} <span className="text-gray-600">vs</span> {opp.platform2}</span>
                  <span className="text-gray-400">{opp.volume}</span>
                </div>
                {opp.url ? (
                  <Button asChild size="sm" className="w-full bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90" data-testid={`trade-button-mobile-${index}`}>
                    <a href={opp.url} target="_blank" rel="noopener noreferrer">Trade</a>
                  </Button>
                ) : (
                  <Button size="sm" disabled className="w-full bg-white/10 text-gray-500" data-testid={`trade-button-mobile-${index}`}>
                    Trade
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full" data-testid="opportunities-table">
              <thead className="sticky top-0 bg-[#06070d] z-10">
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Market</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Platforms</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Spread</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Volume</th>
                  <th className="text-left py-4 px-6 text-gray-400 text-sm font-medium uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {opportunities.map((opp, index) => (
                  <tr 
                    key={`${opp.id}-${index}`}
                    data-testid={`opportunity-row-${index}`}
                    className="border-b border-white/5 hover:bg-white/5 transition-all cursor-pointer"
                  >
                    <td className="py-4 px-6">
                      <div className="text-white font-medium max-w-md">{opp.title}</div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          opp.signal?.direction === 'BUY'
                            ? 'bg-green-500/20 text-green-400'
                            : opp.signal?.direction === 'SELL'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {opp.signal?.direction || 'NEUTRAL'} {((opp.signal?.confidence || 0) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <span className="text-white text-sm">{opp.platform1}</span>
                        <span className="text-gray-600">vs</span>
                        <span className="text-white text-sm">{opp.platform2}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-[#f2e2a6] font-bold text-lg">{opp.spread.toFixed(2)}%</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-400">{opp.volume}</span>
                    </td>
                    <td className="py-4 px-6">
                      {opp.url ? (
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90"
                          data-testid={`trade-button-${index}`}
                        >
                          <a href={opp.url} target="_blank" rel="noopener noreferrer">
                            Trade
                          </a>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled
                          className="bg-white/10 text-gray-500"
                          data-testid={`trade-button-${index}`}
                        >
                          Trade
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            {/* Loading indicator */}
            {isLoading && (
              <div className="text-center py-8" data-testid="loading-indicator">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#f2e2a6]"></div>
                <div className="text-gray-400 mt-2">Loading more opportunities...</div>
              </div>
            )}

            {/* Infinite scroll trigger */}
            <div ref={observerTarget} className="h-4"></div>

            {/* No more data */}
            {!hasMore && opportunities.length > 0 && (
              <div className="text-center py-8" data-testid="no-more-data">
                <div className="text-gray-400">No more opportunities</div>
              </div>
            )}

            {/* No results */}
            {!isLoading && opportunities.length === 0 && (
              <div className="text-center py-12" data-testid="no-results">
                <div className="text-gray-400 text-lg">No opportunities found</div>
                <div className="text-gray-500 text-sm mt-2">Try adjusting your filters</div>
              </div>
            )}
          </div>
        </div>

        {/* Upgrade CTA */}
        <div className="mt-8 bg-gradient-to-r from-[#f2e2a6]/10 to-purple-500/10 border border-[#f2e2a6]/20 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-2">Want More Opportunities?</h3>
          <p className="text-gray-400 mb-6">Upgrade to Pro or Advanced for unlimited access and real-time data</p>
          <Button 
            onClick={() => navigate('/pricing')}
            className="bg-[#f2e2a6] text-black hover:bg-[#f2e2a6]/90 font-semibold px-8 py-6 text-lg"
            data-testid="upgrade-button"
          >
            View Pricing Plans
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
